import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../stores/authStore";
import PcMenu from "./sidebar/PcMenu";
import MobileMenu from "./sidebar/MobileMenu";
import { useDialog } from "../../hooks/useDialog";
import { appConfig, getMenuItems } from "../../config/app.config";

const Layout: React.FC = () => {
  const { t } = useTranslation(["auth", "nav"]);
  const location = useLocation();
  const getCurrentKey = () => {
    const path = location.pathname.replace("/", "");
    return path || "dashboard";
  };
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, logout } = useAuthStore();
  const { confirm } = useDialog();
  const navigate = useNavigate();
  const AppIcon = appConfig.icon.component;

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed));
  }, [collapsed]);

  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const handleLogout = async () => {
    const confirmed = await confirm(t("logout.confirm"), t("logout.title"));
    if (confirmed) {
      logout();
      navigate("/login");
    }
  };

  const handleMenuClick = (key: string) => {
    navigate(`/${key}`);
  };

  return (
    <div className="flex h-screen font-sans">
      {!isMobile && (
        <PcMenu
          collapsed={collapsed}
          menuItems={getMenuItems(t)}
          selectedKey={getCurrentKey()}
          userName={user?.name}
          userRole={user?.role}
          onToggle={() => setCollapsed(!collapsed)}
          onMenuClick={handleMenuClick}
          onLogout={handleLogout}
          onProfileClick={() => navigate("/profile")}
          onChangePasswordClick={() => navigate("/change-password")}
        />
      )}

      <main className="flex-1 flex flex-col min-w-0">
        {isMobile && (
          <header className="bg-bg-surface border-b border-border-subtle px-4 py-3 flex items-center justify-between h-16 shadow-sm">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-bg-elevated transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-text-muted" />
              ) : (
                <Menu className="w-6 h-6 text-text-muted" />
              )}
            </button>
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 bg-gradient-to-br ${appConfig.icon.gradient} rounded-lg flex items-center justify-center shadow-md`}
              >
                <AppIcon className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-base font-bold text-text-primary">
                  {appConfig.name}
                </span>
                <span className="text-xs text-text-muted font-medium leading-none">
                  {appConfig.description}
                </span>
              </div>
            </div>
            <div className="w-10"></div>
          </header>
        )}

        <div className="viewport-scrollbar flex-1 overflow-auto bg-bg-page">
          <Outlet />
        </div>
      </main>

      {isMobile && (
        <MobileMenu
          visible={mobileMenuOpen}
          menuItems={getMenuItems(t)}
          selectedKey={getCurrentKey()}
          userName={user?.name}
          userRole={user?.role}
          onClose={() => setMobileMenuOpen(false)}
          onMenuClick={handleMenuClick}
          onProfileClick={() => navigate("/profile")}
          onChangePasswordClick={() => navigate("/change-password")}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export default Layout;
